import type { UsuarioSessao } from "../types/auth";
import type { Permissoes } from "../types/perfil";

/**
 * Dono/Suporte da plataforma: identidade vem da tabela `configuracoes_suporte_login`
 * (o login marca a sessão com isMasterDev). A antiga regra fixa 1/1/1 foi removida.
 *
 * REGRA PERMANENTE DO PROJETO — o dono do SaaS pode tudo.
 * Toda permissão nova (botão, tela, ação, coluna, filtro, relatório...) nasce
 * com o dono do SaaS liberado, sem depender de checkbox no cadastro de login,
 * de perfil, de empresa ou de organização:
 *
 *   const podeX = usuarioDonoPlataforma(usuario) || Boolean(usuario?.acessoX);
 *
 * No servidor, o par exato desta função é `isPlatformOwner`
 * (backend/src/utils/platform-access.js) — use os dois juntos para que a tela e
 * o RPC concordem sobre quem é o dono. Esconder o botão é conveniência de UI; a
 * checagem que vale é sempre a do servidor.
 */
export function usuarioDonoPlataforma(usuario: UsuarioSessao | null | undefined): boolean {
  return usuario?.isMasterDev === true;
}

export function usuarioPodeAcessarConfiguracoes(usuario: UsuarioSessao | null | undefined): boolean {
  return usuarioDonoPlataforma(usuario);
}

export function usuarioPodeAtenderVendas(usuario: UsuarioSessao | null | undefined): boolean {
  return usuarioTemPermissaoTela(usuario, "vendas/atendimento", "visualizar");
}

/** Vendas DTF é um canal independente do atendimento comercial padrão. */
export function usuarioPodeAtenderVendasDtf(usuario: UsuarioSessao | null | undefined): boolean {
  return usuarioTemPermissaoTela(usuario, "producao/vendas-dtf", "visualizar");
}

export function usuarioPodeUsarChecklists(usuario: UsuarioSessao | null | undefined): boolean {
  return usuarioTemPermissaoTela(usuario, "suporte/checklists", "visualizar");
}

/** A consulta do estoque segue as ações do perfil, sem bloquear outros módulos do login. */
export function usuarioEstoqueSomenteLeituraAtual(
  usuario: UsuarioSessao | null | undefined
): boolean {
  if (!usuario) return false;
  return !usuarioTemPermissaoTela(usuario, "estoque/saldo", "editar")
    && !usuarioTemPermissaoTela(usuario, "estoque/saldo", "excluir");
}

/**
 * Um perfil sem nenhuma tela marcada é tratado como "sem restrição" (mesma
 * convenção usada na janela de horário: campo em branco = acesso livre) —
 * evita travar perfis cadastrados antes desta trava existir.
 */
export function usuarioTemAcessoTela(
  usuario: UsuarioSessao | null | undefined,
  chaveTela: string
): boolean {
  if (!usuario) return false;
  if (usuarioDonoPlataforma(usuario)) return true;
  return usuarioTemPermissaoTela(usuario, chaveTela, "visualizar");
}

/**
 * Encontra as permissões cadastradas para `chaveTela`, subindo por prefixos
 * quando a chave exata não existe no mapa. Sub-rotas de criação/edição (ex.:
 * "cadastros/checklists/novo" ou "cadastros/checklists/42") nunca aparecem
 * sozinhas no Perfil de Acesso — só a tela-mãe ("cadastros/checklists") é
 * cadastrada lá — então sem essa busca elas seriam tratadas como uma tela
 * nunca liberada, mesmo com a tela-mãe 100% permitida.
 */
function permissoesDaTela(
  mapa: Record<string, Permissoes>,
  chaveTela: string
): Permissoes | undefined {
  if (Object.prototype.hasOwnProperty.call(mapa, chaveTela)) return mapa[chaveTela];
  // A permissão explícita da listagem prevalece sobre a chave antiga do dashboard.
  const dashboard = chaveTela === "fornecedor/pedidos" ? "fornecedor/visao-geral"
    : chaveTela === "estoque/saldo" ? "estoque/visao-geral"
    : chaveTela === "financeiro/fluxo-caixa-diario-mensal" ? "financeiro/visao-geral"
    : chaveTela === "producao/lista" ? "producao/visao-geral"
    : ["vendas", "compras", "suporte"].includes(chaveTela) ? `${chaveTela}/visao-geral` : null;
  if (dashboard && Object.prototype.hasOwnProperty.call(mapa, dashboard)) {
    return mapa[dashboard];
  }
  const partes = chaveTela.split("/").filter(Boolean);
  for (let fim = partes.length - 1; fim > 0; fim -= 1) {
    const prefixo = partes.slice(0, fim).join("/");
    if (Object.prototype.hasOwnProperty.call(mapa, prefixo)) return mapa[prefixo];
  }
  return undefined;
}

/**
 * Checa se `usuario` pode executar `acao` na tela `chaveTela`. Mesma
 * convenção de `usuarioTemAcessoTela`: mapa de permissões vazio/ausente
 * (perfil legado ainda não migrado) = sem restrição, todas as ações
 * liberadas. Mapa não vazio e tela ausente = sem acesso à tela. Tela
 * presente = usa as ações salvas, com `visualizar` default `true`.
 */
export function usuarioTemPermissaoTela(
  usuario: UsuarioSessao | null | undefined,
  chaveTela: string,
  acao: keyof Permissoes
): boolean {
  if (!usuario) return false;
  if (usuarioDonoPlataforma(usuario)) return true;
  const mapa = usuario?.permissoesTelas ?? {};
  const chaves = Object.keys(mapa);
  if (chaves.length === 0) return true;
  const permissoesTela = permissoesDaTela(mapa, chaveTela);
  if (!permissoesTela) return false;
  if (acao === "visualizar" && permissoesTela.visualizar === undefined) return true;
  /* Perfil salvo antes de "novo" existir: cai no valor de "editar". */
  if (acao === "novo" && permissoesTela.novo === undefined) return !!permissoesTela.editar;
  return !!permissoesTela[acao];
}

/** Visibilidade de chamados e tarefas é concedida pelo perfil, dentro das empresas autorizadas. */
export function usuarioVeChamadosDeTodos(usuario: UsuarioSessao | null | undefined): boolean {
  return ["suporte", "suporte/kanban", "suporte/calendario"].some((tela) =>
    usuarioTemPermissaoTela(usuario, tela, "visualizar")
  );
}

/**
 * Autoriza o quadro geral da Produção pela ação de edição do Perfil de Acesso.
 */
export function usuarioEquipeProducao(usuario: UsuarioSessao | null | undefined): boolean {
  return usuarioTemPermissaoTela(usuario, "producao/kanban", "editar");
}

/** Configuração compartilhada entre as telas de um módulo no Perfil de Acesso. */
export function usuarioExigeAutorizacaoAdmin(
  usuario: UsuarioSessao | null | undefined,
  modulo: "vendas" | "financeiro"
): boolean {
  if (usuarioDonoPlataforma(usuario)) return false;
  return Object.entries(usuario?.permissoesTelas ?? {}).some(([chave, permissoes]) =>
    (chave === modulo || chave.startsWith(`${modulo}/`))
    && permissoes.exigirAutorizacaoAdmin === true
  );
}

/**
 * Empresa que deve vir travada nos cadastros (novo chamado, nova tarefa): a
 * empresa ativa da sessão. Vazio quando a sessão está em "todos" — aí não há uma
 * empresa única e o campo precisa continuar selecionável.
 */
export function empresaTravadaDaSessao(usuario: UsuarioSessao | null | undefined): string {
  if (usuarioDonoPlataforma(usuario)) return "";
  const atual = String(usuario?.codEmpresa ?? "").trim();
  if (!atual || atual.toLowerCase() === "todos") return "";
  return atual;
}

/** Retorna "todas" para quem enxerga toda a organização, ou a lista de empresas liberadas para o login. */
export function empresasPermitidasUsuario(
  usuario: UsuarioSessao | null | undefined
): "todas" | string[] {
  if (!usuario) return [];
  if (usuarioDonoPlataforma(usuario) || usuario.acessoGlobal) return "todas";
  const lista = Array.isArray(usuario.empresasAcesso) ? usuario.empresasAcesso.map(String).filter(Boolean) : [];
  if (lista.length) return lista;
  const principal = String(usuario.codEmpresaLogin ?? usuario.codEmpresa ?? "");
  return principal ? [principal] : [];
}
